import { c, t, sp, r, card } from '../../theme';

// A KPI tile that can be a way in rather than a dead end.
//
// Passing `onClick` makes the tile a real <button>: it takes keyboard focus,
// and the cursor and hover tell you it does something. Without one it renders
// as a plain div, so a figure with nothing behind it never looks clickable —
// the fastest way to lose a reader's trust in a board is a tile that ignores
// them.
//
// `tone` colors the icon well, not the number. A red $1.2M reads as an error
// state; the value is just a value, and it is the category that carries the
// warning.
const TONES = {
  neutral: { fg: c.info,        bg: c.infoTint },
  warning: { fg: c.warningText, bg: c.warningTint },
  danger:  { fg: c.dangerText,  bg: c.dangerTint },
  success: { fg: c.successText, bg: c.successTint },
};

// Change against the previous snapshot.
//
// `goodDirection` says which way is an improvement, and it is deliberately
// optional: total inventory value has no agreed direction until there is a
// reduction target to judge it against, so that tile shows the movement in
// neutral grey rather than implying a verdict nobody has set. The exception
// tiles do have a direction — fewer parts below minimum is better, always.
function Delta({ current, previous, goodDirection }) {
  if (previous === null || previous === undefined) return null;
  const prev = Number(previous);
  const curr = Number(current);
  if (!prev) return null;

  const pct = ((curr - prev) / Math.abs(prev)) * 100;
  if (Math.abs(pct) < 0.05) return <span style={s.deltaFlat}>no change</span>;

  const up   = pct > 0;
  const good = goodDirection ? (goodDirection === 'up' ? up : !up) : null;
  const color = good === null ? c.textMuted : (good ? c.successText : c.dangerText);

  return (
    <span style={{ ...s.delta, color }}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export default function StatTile({
  icon, label, value, sub, tone = 'neutral', onClick, active = false, title,
  current, previous, goodDirection,
}) {
  const palette = TONES[tone] || TONES.neutral;
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      aria-pressed={onClick ? active : undefined}
      style={{
        ...s.tile,
        ...(onClick ? s.clickable : null),
        ...(active ? s.active : null),
      }}
    >
      <div style={{ ...s.icon, color: palette.fg, background: palette.bg }}>{icon}</div>
      <div style={s.body}>
        <div style={s.valueRow}>
          <span style={s.value}>{value}</span>
          <Delta current={current} previous={previous} goodDirection={goodDirection} />
        </div>
        <div style={s.label}>{label}</div>
        {sub && <div style={s.sub}>{sub}</div>}
      </div>
    </Tag>
  );
}

const s = {
  tile: {
    ...card.base,
    // card.base sets the `border` SHORTHAND, while `active` below overrides only
    // borderColor. React warns about the mix, and it is a real fault rather than
    // a lint nit: deselecting a tile removes the longhand while the shorthand
    // still applies, which can leave the highlight colour behind. Restated in
    // longhand so both states speak the same language.
    border: undefined,
    borderWidth: '1px', borderStyle: 'solid', borderColor: c.border,
    padding: `${sp[4]} ${sp[4]}`,
    display: 'flex', alignItems: 'flex-start', gap: sp[3],
    textAlign: 'left', width: '100%', fontFamily: 'inherit',
  },
  clickable: { cursor: 'pointer', transition: 'border-color 120ms, box-shadow 120ms' },
  active:    { borderColor: c.navy, boxShadow: `inset 0 0 0 1px ${c.navy}` },
  icon: {
    width: '34px', height: '34px', borderRadius: r.lg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  body:     { minWidth: 0 },
  valueRow: { display: 'flex', alignItems: 'baseline', gap: sp[2], flexWrap: 'wrap' },
  value:    { ...t.h2, ...t.tabular, color: c.text },
  delta:    { ...t.micro, ...t.tabular, fontWeight: 600, whiteSpace: 'nowrap' },
  deltaFlat:{ ...t.micro, color: c.textFaint, whiteSpace: 'nowrap' },
  label: { ...t.micro, color: c.textMuted, marginTop: '1px' },
  sub:   { ...t.micro, color: c.textFaint, marginTop: '3px', lineHeight: 1.35 },
};
